import type { Node as NodeSchema } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';

/**
 * Watches for node deletions and triggers a callback function when a node is deleted.
 *
 * @param onDelete - The callback function to be executed when a node is deleted.
 * @param isEqual - A function that checks if two nodes are equal.
 * @param isNodeType - A function that checks if a node matches a specific node type.
 * @returns A Plugin object that watches for node deletions.
 */
export function watchNodeDeletion({
  onDelete,
  isEqual,
  isNodeType: match,
}: {
  onDelete: (node: NodeSchema) => Promise<void>;
  isNodeType: (node: NodeSchema) => boolean;
  isEqual: (a: NodeSchema, b: NodeSchema) => boolean;
}) {
  return new Plugin({
    view(editorView) {
      let previousNodes = Array<NodeSchema>();

      editorView.state.doc.descendants((node) => {
        if (match(node)) previousNodes.push(node);
      });

      return {
        update(view) {
          const newState = view.state;
          const currentNodes = Array<NodeSchema>();

          newState.doc.descendants((node) => {
            if (match(node)) currentNodes.push(node);
          });

          const deletedNotes = Array.from(previousNodes).filter((node) => !currentNodes.some((n) => isEqual(node, n)));

          if (deletedNotes.length) {
            deletedNotes.forEach((node) => {
              onDelete(node).catch((error) => {
                console.error('Failed to delete node', error);
              });
            });
          }

          previousNodes = currentNodes;
        },
      };
    },
  });
}
